import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { formatUSD, formatLocal } from "@/lib/utils";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, XCircle, Calculator, X, RefreshCw, Clock, Phone, Edit3 } from "lucide-react";
import StatusPill from "@/components/StatusPill";
import ReceiptActions from "@/components/ReceiptActions";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";

// ---------------------------------------------------------------------------
// Display helpers — clean labels coming from DingConnect catalog
// ---------------------------------------------------------------------------
function cleanOperatorName(name = "") {
  // Strip the "(CLP)" technical suffix. Keep meaningful suffixes like "(Prepaid Plans)".
  return name.replace(/\s*\(CLP\)\s*/i, "").trim();
}

function cleanProductLabel(product) {
  // If DingConnect's default text is just a currency price range like
  // "HTG 138.68-13917.72" or "USD 5.00", show "Recharge" instead.
  const text = (product?.default_display_text || product?.name || "").trim();
  const looksLikePriceRange = /^[A-Z]{2,4}\s*\d+(\.\d+)?\s*[-–]\s*\d+(\.\d+)?$/i.test(text) ||
    /^[A-Z]{2,4}\s*\d+(\.\d+)?$/i.test(text);
  if (!text || looksLikePriceRange) return "Recharge";
  return text;
}

// Display helper — NANP countries (US/CA/DO/JM etc.) store full-area-code
// prefixes like "+1201"/"+1800" in DingConnect data so operator detection
// works. For display we collapse them to the canonical "+1" expected by users.
function displayDialCode(dial) {
  const s = (dial || "").trim();
  if (!s) return "";
  if (/^\+1\d{2,}$/.test(s)) return "+1";
  return s;
}

// Debounce helper — fires fn N ms after the user stops typing.
function debounce(fn, ms = 350) {
  let t;
  const wrapped = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
  wrapped.cancel = () => clearTimeout(t);
  return wrapped;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SendTopup({ category = "airtime", pageTitle, pageSubtitle } = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMaster = user?.role === "MASTER_AGENT";
  const [countries, setCountries] = useState([]);
  const [operators, setOperators] = useState([]);
  const [products, setProducts] = useState([]);
  const [country, setCountry] = useState("");
  const [operator, setOperator] = useState("");
  const [product, setProduct] = useState("");
  const [phone, setPhone] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);
  const [detection, setDetection] = useState(null);   // backend response cache
  const [detecting, setDetecting] = useState(false);

  // `category` isolates the product taxonomy shown in this instance:
  //   'airtime'   → Send Topup (mobile / bundles)
  //   'giftcards' → Gift Cards (Amazon, Netflix, etc.)
  //   'bills'     → Utilities & Bills (Electricity, Water, TV, etc.)
  const categoryParam = category && category !== "airtime" ? `?category=${category}` : "";
  const categorySuffix = category && category !== "airtime" ? `&category=${category}` : "";

  useEffect(() => {
    api.get(`/catalog/countries${categoryParam}`).then((r) => setCountries(r.data));
  }, [categoryParam]);

  // Backend-driven operator detection (debounced).
  const detectRef = useRef(null);
  useEffect(() => {
    if (!detectRef.current) {
      detectRef.current = debounce(async (rawPhone) => {
        try {
          setDetecting(true);
          const { data } = await api.get(
            `/catalog/detect-operator?phone=${encodeURIComponent(rawPhone)}`,
          );
          setDetection(data);
        } catch {
          setDetection(null);
        } finally {
          setDetecting(false);
        }
      }, 300);
    }
    if (manualOverride) return;
    if (!phone || phone.replace(/\D/g, "").length < 4) {
      setDetection(null);
      detectRef.current.cancel?.();
      return;
    }
    detectRef.current(phone);
  }, [phone, manualOverride]);

  // Apply detection result → set country + operator when unambiguous.
  useEffect(() => {
    if (manualOverride || !detection) return;
    const c = detection.country?.code;
    if (c && c !== country) setCountry(c);
    if (!detection.ambiguous && detection.candidates?.length === 1) {
      const opId = detection.candidates[0].operator?.id;
      if (opId && opId !== operator) setOperator(opId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detection, manualOverride]);

  // When operators load and we still have no selection, fall back to:
  //   - sole operator -> auto-pick
  //   - otherwise leave unset (user picks from dropdown)
  useEffect(() => {
    if (manualOverride) return;
    if (operators.length === 1 && operators[0].id !== operator) {
      setOperator(operators[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operators, manualOverride]);

  // When products load, auto-select the first one (usually "Recharge").
  useEffect(() => {
    if (products.length > 0 && !product) {
      setProduct(products[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useEffect(() => {
    if (!manualOverride) {
      setOperator("");
    }
    setProduct("");
    setOperators([]);
    setProducts([]);
    setEstimate(null);
    if (country) api.get(`/catalog/operators?country_code=${country}${categorySuffix}`).then((r) => setOperators(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  useEffect(() => {
    setProduct("");
    setProducts([]);
    setEstimate(null);
    if (operator) api.get(`/catalog/products?operator_id=${operator}${categorySuffix}`).then((r) => setProducts(r.data));
  }, [operator]);

  const selectedCountry = useMemo(
    () => countries.find((c) => c.code === country),
    [countries, country]
  );
  const selectedOperator = useMemo(
    () => operators.find((o) => o.id === operator),
    [operators, operator]
  );

  // When the user changes country MANUALLY, auto-prepend the dial code so
  // they don't have to type +509 / +56 / +1 etc themselves.
  const onCountryChange = (code) => {
    setCountry(code);
    setManualOverride(true);
    setOperator("");
    setProduct("");
    setEstimate(null);
    const c = countries.find((x) => x.code === code);
    const dial = (c?.dial_code || "").trim();
    if (dial) {
      const displayed = displayDialCode(dial);
      const digits = phone.replace(/\D/g, "");
      const dialDigits = displayed.replace(/\D/g, "");
      // If phone is empty OR doesn't start with the new dial code → reset to dial code
      if (!digits || !digits.startsWith(dialDigits)) {
        setPhone(displayed + " ");
      }
    }
  };
  const selectedProduct = useMemo(
    () => products.find((p) => p.id === product),
    [products, product]
  );
  const isRange = selectedProduct && !selectedProduct.fixed;

  const doEstimate = async () => {
    if (!product || !phone) {
      toast.error("Select a product and enter a phone number");
      return;
    }
    setBusy(true);
    try {
      const payload = { product_id: product, phone_number: phone };
      if (isRange && customAmount) payload.send_value_usd = parseFloat(customAmount);
      const { data } = await api.post("/topup/estimate", payload);
      setEstimate(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Estimate failed");
    } finally {
      setBusy(false);
    }
  };

  const doSend = async () => {
    if (!estimate) return;
    setBusy(true);
    setResult(null);
    setShowConfirm(false);
    try {
      const payload = {
        country_code: country,
        operator_id: operator,
        product_id: product,
        phone_number: phone,
      };
      if (isRange && customAmount) payload.send_value_usd = parseFloat(customAmount);
      const { data } = await api.post("/topup/send", payload);
      setResult(data);
      if (data.status === "SUCCESS") toast.success("Topup delivered!");
      else if (data.status === "FAILED") toast.error("Topup failed — funds reversed");
      else toast.message("Topup pending");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setEstimate(null);
    setPhone("");
    setProduct("");
    setCustomAmount("");
    setManualOverride(false);
    setCountry("");
    setOperator("");
  };

  const selectClass =
    "flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400";

  const cleanedOperatorName = cleanOperatorName(selectedOperator?.name);
  const cleanedProductName = selectedProduct ? cleanProductLabel(selectedProduct) : "";

  const headerTitle = pageTitle || t("topup.title");
  const headerSubtitle = pageSubtitle || t("topup.subtitle");

  return (
    <div className="space-y-6" data-testid="send-topup-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{headerTitle}</div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {headerTitle}
        </h1>
        <p className="text-slate-500 text-sm mt-1">{headerSubtitle}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5">
          {/* STEP 1 — Destination number */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#0F62FE] text-white text-[10px] font-bold">1</span>
              Destination number
              {selectedCountry?.dial_code && (
                <span
                  data-testid="topup-prefix-chip"
                  className="ml-1 text-[10px] font-bold tracking-wider bg-[#EDF5FF] text-[#0F62FE] px-2 py-0.5 rounded-full border border-[#0F62FE]/20"
                >
                  {selectedCountry.name} · {displayDialCode(selectedCountry.dial_code)}
                </span>
              )}
            </label>
            <div className="relative flex items-stretch">
              {selectedCountry?.dial_code ? (
                <div
                  data-testid="topup-prefix-badge"
                  className="inline-flex items-center justify-center px-3 rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 text-sm font-bold font-mono text-slate-700 select-none min-w-[64px]"
                  title={`${selectedCountry.name}`}
                >
                  {displayDialCode(selectedCountry.dial_code)}
                </div>
              ) : (
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
              )}
              <input
                data-testid="topup-phone-input"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setEstimate(null); }}
                placeholder={selectedCountry?.dial_code ? t("topup.phone_placeholder") : "+50930000000"}
                className={`${selectClass} ${selectedCountry?.dial_code ? "rounded-l-none" : "pl-9"} flex-1`}
                inputMode="tel"
                autoFocus
              />
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              {selectedCountry?.dial_code
                ? `Country code ${displayDialCode(selectedCountry.dial_code)} is applied for ${selectedCountry.name}. Type the local number.`
                : "Type the full international number — country and operator are detected automatically."}
            </div>

            {/* Prominent detection banner — confirms the detected country
                (and operator when unambiguous) so the agent can eyeball the
                match before proceeding. Only shown for auto-detected flows;
                a manual override hides it since the agent already picked. */}
            {!manualOverride && detection?.country && (
              <div
                className="mt-2 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs"
                data-testid="topup-detected-country-banner"
              >
                {detection.country.iso2 && (
                  <img
                    src={`https://flagcdn.com/w40/${detection.country.iso2}.png`}
                    alt=""
                    className="w-5 h-4 rounded-sm object-cover flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                <span className="font-semibold text-emerald-900">
                  {detection.country.name || detection.country.code}
                </span>
                <span className="text-emerald-700">
                  · {displayDialCode(detection.country.dial_code || "")}
                </span>
                {!detection.ambiguous && detection.candidates?.[0]?.operator?.name && (
                  <span className="ml-auto text-emerald-800 font-semibold truncate">
                    → {cleanOperatorName(detection.candidates[0].operator.name)}
                  </span>
                )}
                {detection.ambiguous && (
                  <span className="ml-auto text-amber-700 font-semibold">
                    Choose operator ↓
                  </span>
                )}
              </div>
            )}
          </div>

          {/* STEP 2 — Detected country + operator (read-only chips with Change link) */}
          {(country || manualOverride) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#0F62FE] text-white text-[10px] font-bold">2</span>
                  {t("topup.country")}
                  {!manualOverride && country && (
                    <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-bold tracking-wider">AUTO</span>
                  )}
                </label>
                <select
                  data-testid="topup-country-select"
                  value={country}
                  onChange={(e) => onCountryChange(e.target.value)}
                  className={selectClass}
                >
                  <option value="">{t("topup.select_country")}</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.dial_code ? `${displayDialCode(c.dial_code)} · ${c.name}` : c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                  {t("topup.operator")}
                  {!manualOverride && operator && operators.length === 1 && (
                    <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-bold tracking-wider">AUTO</span>
                  )}
                  {!manualOverride && operator && detection && !detection.ambiguous && operators.length > 1 && (
                    <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-bold tracking-wider">AUTO</span>
                  )}
                  {operator && operators.length > 1 && (
                    <button
                      type="button"
                      onClick={() => { setManualOverride(true); setOperator(""); }}
                      className="text-[10px] text-[#0F62FE] hover:underline inline-flex items-center gap-1"
                      data-testid="topup-change-operator"
                    >
                      <Edit3 className="w-3 h-3" /> change
                    </button>
                  )}
                </label>

                {/* Hide selector when sole or unambiguously auto-selected; show a read-only badge instead */}
                {operator && operators.length === 1 && !manualOverride ? (
                  <div
                    data-testid="topup-operator-badge"
                    className="h-11 flex items-center px-3 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-semibold text-emerald-800"
                  >
                    {cleanOperatorName(operators[0]?.name)}
                  </div>
                ) : (
                  <select
                    data-testid="topup-operator-select"
                    value={operator}
                    onChange={(e) => { setOperator(e.target.value); setProduct(""); setEstimate(null); setManualOverride(true); }}
                    disabled={!country}
                    className={selectClass}
                  >
                    <option value="">{t("topup.select_operator")}</option>
                    {operators.map((o) => (
                      <option key={o.id} value={o.id}>
                        {cleanOperatorName(o.name)}
                      </option>
                    ))}
                  </select>
                )}

                {operators.length > 1 && detection?.ambiguous && !operator && (
                  <>
                    <div
                      data-testid="topup-operator-suggestions"
                      className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5"
                    >
                      {detecting
                        ? "Detecting operator…"
                        : `${operators.length} operators for ${selectedCountry?.name || "this country"} — tap one to load its catalog:`}
                    </div>
                    {/* Quick-choice grid — one button per operator so the
                        agent picks without opening the dropdown. Shows the
                        official carrier logo (from DingConnect) so it's
                        recognisable at a glance. */}
                    <div
                      className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2"
                      data-testid="topup-operator-quickpick"
                    >
                      {operators.map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => {
                            setOperator(o.id);
                            setProduct("");
                            setEstimate(null);
                            setManualOverride(true);
                          }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#0F62FE] hover:bg-blue-50 text-left transition-colors"
                          data-testid={`topup-operator-quickpick-${o.id}`}
                        >
                          {o.logo_url ? (
                            <img
                              src={o.logo_url}
                              alt=""
                              className="w-7 h-7 rounded object-contain flex-shrink-0 bg-slate-50"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <div className="w-7 h-7 rounded bg-slate-100 flex-shrink-0" />
                          )}
                          <span className="text-xs font-semibold text-slate-800 truncate">
                            {cleanOperatorName(o.name)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* STEP 3 — Product */}
          {operator && (
            <div>
              <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#0F62FE] text-white text-[10px] font-bold">3</span>
                {t("topup.product")}
              </label>
              <select
                data-testid="topup-product-select"
                value={product}
                onChange={(e) => { setProduct(e.target.value); setEstimate(null); }}
                disabled={!operator}
                className={selectClass}
              >
                <option value="">{t("topup.select_product")}</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {cleanProductLabel(p)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* STEP 4 — Amount range + amount to send */}
          {selectedProduct && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <label className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#0F62FE] text-white text-[10px] font-bold">4</span>
                    Amount range (agent · USD)
                  </label>
                  <div
                    data-testid="topup-amount-range"
                    className="text-slate-900 font-semibold mt-0.5"
                  >
                    USD {selectedProduct.min_send_usd?.toFixed(2)}
                    {!selectedProduct.fixed && (
                      <span> – USD {selectedProduct.max_send_usd?.toFixed(2)}</span>
                    )}
                  </div>
                </div>
                {isRange && (
                  <div className="w-full sm:w-auto sm:min-w-[200px]">
                    <label className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#0F62FE] text-white text-[10px] font-bold">5</span>
                      Amount to send (USD)
                    </label>
                    <input
                      data-testid="topup-custom-amount-input"
                      type="number"
                      step="0.01"
                      min={selectedProduct.min_send_usd}
                      max={selectedProduct.max_send_usd}
                      value={customAmount}
                      onChange={(e) => { setCustomAmount(e.target.value); setEstimate(null); }}
                      placeholder={`${selectedProduct.min_send_usd?.toFixed(2)}`}
                      className="flex h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 6 — Estimate / Send */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              data-testid="topup-estimate-button"
              onClick={doEstimate}
              disabled={busy || !product || !phone}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-[#0F62FE] text-[#0F62FE] font-semibold hover:bg-[#EDF5FF] disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              {busy ? t("topup.estimating") : t("topup.estimate")}
            </button>
            <button
              data-testid="topup-send-button"
              onClick={() => setShowConfirm(true)}
              disabled={busy || !estimate}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {busy ? t("topup.sending") : t("topup.send")}
            </button>
            {(estimate || result) && (
              <button
                onClick={reset}
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50"
              >
                {t("common.reset")}
              </button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {estimate && (
            <div
              data-testid="topup-estimate-card"
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              <div className="px-5 py-3 border-b border-slate-200 bg-[#EDF5FF]">
                <div className="text-xs font-semibold uppercase tracking-widest text-[#0F62FE]">
                  {t("topup.estimate")}
                </div>
              </div>
              <div className="p-5 space-y-3 text-sm">
                <Row k={t("topup.operator")} v={cleanedOperatorName || estimate.product?.operator_id} />
                <Row k={t("topup.phone")} v={phone} mono />
                <Row k={t("topup.product")} v={cleanedProductName || t("topup.product")} />

                {/* CUSTOMER-FACING amount — most prominent */}
                <div className="rounded-lg bg-[#EDF5FF] border border-[#0F62FE]/20 px-4 py-3 mt-2">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0F62FE]">
                    {t("topup.amount_to_send")}
                  </div>
                  <div
                    data-testid="topup-customer-pays"
                    className="text-2xl font-bold text-[#0F62FE] mt-0.5"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {formatLocal(estimate.customer_pays_local, estimate.local_currency)}
                  </div>
                  {estimate.local_currency !== "USD" && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      {t("settings.exchange_rate")}: 1 USD = {estimate.exchange_rate} {estimate.local_currency}
                    </div>
                  )}
                </div>

                {estimate.receive_value ? (
                  <Row
                    k={t("topup.recipient_receives")}
                    v={`${Number(estimate.receive_value).toFixed(2)} ${estimate.receive_currency || ""}`}
                    emphasis
                  />
                ) : null}

                {/* INTERNAL agent-only block */}
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
                    {t("topup.commission")}
                  </div>
                  <div className="space-y-2">
                    <Row k={t("topup.cost_usd")} v={`${Number(estimate.send_value).toFixed(2)} USD`} />
                    <Row
                      k={t("topup.commission")}
                      v={`${estimate.commission_rate}% · ${formatUSD(estimate.commission_amount)}`}
                    />
                    <Row k="Net wallet cost" v={formatUSD(estimate.net_cost_usd)} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {result && (
            <ResultCard
              initial={result}
              onUpdate={(r) => setResult(r)}
            />
          )}
        </div>
      </div>

      {showConfirm && estimate && (
        <ConfirmModal
          onCancel={() => setShowConfirm(false)}
          onConfirm={doSend}
          busy={busy}
          phone={phone}
          operator={cleanedOperatorName || selectedOperator?.name}
          customerPays={formatLocal(estimate.customer_pays_local, estimate.local_currency)}
          customerReceives={
            estimate.receive_value
              ? `${Number(estimate.receive_value).toFixed(2)} ${estimate.receive_currency || ""}`
              : "—"
          }
          internalCost={formatUSD(estimate.net_cost_usd)}
        />
      )}
    </div>
  );
}

function Row({ k, v, mono, emphasis }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-slate-500">{k}</div>
      <div
        className={
          "text-right " +
          (emphasis ? "text-emerald-700 font-semibold " : "text-slate-900 ") +
          (mono ? "font-mono text-xs" : "")
        }
      >
        {v}
      </div>
    </div>
  );
}

function ResultCard({ initial, onUpdate }) {
  const [topup, setTopup] = useState(initial);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);
  const { brand } = useBranding();

  useEffect(() => {
    setTopup(initial);
  }, [initial]);

  const refresh = async (silent = false) => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(`/topups/${topup.id}/refresh-status`);
      const fresh = data.topup || topup;
      setTopup(fresh);
      onUpdate?.(fresh);
      if (!silent && data.updated) {
        if (fresh.status === "SUCCESS") toast.success("Topup confirmed delivered!");
        else if (fresh.status === "FAILED" || fresh.status === "CANCELLED")
          toast.error(`Topup ${fresh.status.toLowerCase()} — funds reversed`);
      } else if (!silent) {
        toast.message(`Still processing on the ${brand.brand_name} Network…`);
      }
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-poll every 10s while PENDING (cap at ~10 minutes).
  useEffect(() => {
    clearInterval(intervalRef.current);
    if (topup.status !== "PENDING") return;
    const startedAt = Date.now();
    intervalRef.current = setInterval(() => {
      if (Date.now() - startedAt > 10 * 60 * 1000) {
        clearInterval(intervalRef.current);
        return;
      }
      refresh(true);
    }, 10000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topup.status, topup.id]);

  if (topup.status === "PENDING") return <PendingCard topup={topup} onRefresh={() => refresh(false)} refreshing={refreshing} brandName={brand.brand_name} />;
  if (topup.status === "SUCCESS") return <SuccessReceipt topup={topup} brandName={brand.brand_name} />;
  return <FailedCard topup={topup} />;
}

function PendingCard({ topup, onRefresh, refreshing, brandName }) {
  const txRef = topup.transaction_reference || topup.provider_transfer_ref || topup.provider_tx_id || (topup.id || "").replace(/-/g, "").toUpperCase().slice(0, 12);
  return (
    <div
      data-testid="topup-pending-card"
      className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-slate-200 bg-amber-50 flex items-center gap-2">
        <Clock className="w-5 h-5 text-amber-600 animate-pulse" />
        <div className="text-sm font-semibold text-slate-900">Processing transaction</div>
        <div className="ml-auto"><StatusPill status="PENDING" /></div>
      </div>
      <div className="p-5 space-y-3 text-sm">
        <div className="text-slate-600">
          The {brandName} Network has accepted the transfer and is delivering it to the operator.
          The final receipt will appear once delivery is confirmed.
        </div>
        <div className="border-t border-slate-200 pt-3 space-y-2">
          <Row k="Transaction reference" v={txRef} mono />
        </div>
      </div>
      <div className="px-5 pb-5">
        <button
          data-testid="topup-refresh-status-btn"
          onClick={onRefresh}
          disabled={refreshing}
          className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg border border-[#0F62FE] text-[#0F62FE] text-sm font-semibold hover:bg-[#EDF5FF] disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Check status now
        </button>
        <div className="mt-2 text-[11px] text-slate-500 text-center">
          Auto-checking every 10 seconds. We won&apos;t send another topup.
        </div>
      </div>
    </div>
  );
}

function SuccessReceipt({ topup, brandName }) {
  const dateTime = topup.completed_at || topup.updated_at || topup.created_at;
  const txRef = topup.transaction_reference || topup.provider_transfer_ref || topup.provider_tx_id || (topup.id || "").replace(/-/g, "").toUpperCase().slice(0, 12);
  const localCurrency = (topup.local_currency || "USD").toUpperCase();
  const customerPaysLocal = topup.customer_pays_local != null
    ? topup.customer_pays_local
    : Number(topup.retail_price_usd || topup.send_value || 0) * Number(topup.exchange_rate || 1);
  return (
    <div
      data-testid="topup-success-receipt"
      className="bg-white rounded-xl border-2 border-emerald-300 shadow-sm overflow-hidden"
    >
      <div className="px-5 py-4 border-b border-emerald-200 bg-emerald-50 flex items-center gap-2">
        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
        <div>
          <div className="text-sm font-semibold text-emerald-900">Topup delivered</div>
          <div className="text-xs text-emerald-700">Confirmed by the {brandName} Network · final receipt</div>
        </div>
        <div className="ml-auto"><StatusPill status="SUCCESS" /></div>
      </div>
      <div className="p-5 space-y-2 text-sm">
        <Row k="Reference" v={txRef} mono />
        <Row k="Operator" v={cleanOperatorName(topup.operator_name)} />
        <Row k="Destination number" v={topup.phone_number} mono />
        {topup.receive_value ? (
          <Row
            k="Customer receives"
            v={`${Number(topup.receive_value).toFixed(2)} ${topup.receive_currency || ""}`}
            emphasis
          />
        ) : null}
        <Row k="Date / time" v={formatReceiptDate(dateTime)} />
        <div className="border-t border-slate-200 pt-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Amount paid
            </div>
            <div
              data-testid="receipt-customer-paid"
              className="text-xl font-bold text-emerald-700"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {formatLocal(customerPaysLocal, localCurrency)}
            </div>
          </div>
        </div>
      </div>
      <div className="px-5 pb-5 border-t border-slate-200 pt-4 mt-1">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
          Receipt actions
        </div>
        <ReceiptActions topupId={topup.id} />
      </div>
    </div>
  );
}

function FailedCard({ topup }) {
  const txRef = topup.transaction_reference || (topup.id || "").replace(/-/g, "").toUpperCase().slice(0, 12);
  const reason = topup.agent_message || "Transaction was not completed. Please verify the number and try again.";
  return (
    <div
      data-testid="topup-failed-card"
      className="bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-red-200 bg-red-50 flex items-center gap-2">
        <XCircle className="w-5 h-5 text-red-600" />
        <div>
          <div className="text-sm font-semibold text-red-900">Topup failed</div>
          <div className="text-xs text-red-700">Wallet hold has been reversed automatically</div>
        </div>
        <div className="ml-auto"><StatusPill status={topup.status} /></div>
      </div>
      <div className="p-5 space-y-2 text-sm">
        <Row k="Reason" v={reason} />
        <Row k="Transaction reference" v={txRef} mono />
        <Row k="Destination number" v={topup.phone_number} mono />
        <Row k="Operator" v={cleanOperatorName(topup.operator_name)} />
        <Row k="Reversed amount" v={formatUSD(topup.net_cost_usd)} emphasis />
        {topup.admin_diagnostic && (
          <div
            data-testid="admin-diagnostic"
            className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            <div className="font-semibold uppercase tracking-widest text-[10px] text-amber-700 mb-1">
              Admin diagnostic
            </div>
            {topup.admin_diagnostic}
          </div>
        )}
        {Array.isArray(topup.failover_trace) && topup.failover_trace.length > 0 && (
          <div data-testid="failover-trace" className="mt-3 border-t border-red-100 pt-3">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
              Delivery route trace
            </div>
            <ol className="space-y-1.5">
              {topup.failover_trace.map((s) => (
                <li
                  key={s.step}
                  data-testid={`failover-step-${s.step}`}
                  className="flex items-start gap-2 text-xs"
                >
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold flex-shrink-0">
                    {s.step}
                  </span>
                  <span>
                    <span className="font-semibold text-slate-800 capitalize">{s.provider}</span>
                    <span className="text-slate-500"> — {s.label}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

function formatReceiptDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function ConfirmModal({ onCancel, onConfirm, busy, phone, operator, customerPays, customerReceives, internalCost }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={busy ? undefined : onCancel} />
      <div
        data-testid="topup-confirm-modal"
        className="relative bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div
              className="text-lg font-semibold text-slate-900"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Confirm topup
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Review the details before submitting to the network.
            </div>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Row k="Destination number" v={phone} mono />
          <Row k="Operator" v={operator} />
          <Row k="Customer receives" v={customerReceives} emphasis />
          <div className="border-t border-slate-200 pt-3 flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Customer pays
            </div>
            <div
              data-testid="topup-confirm-customer-pays"
              className="text-2xl font-bold text-[#0F62FE]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {customerPays}
            </div>
          </div>
          <div className="text-[11px] text-slate-400 text-right">
            Internal wallet cost: <span className="font-semibold text-slate-600">{internalCost}</span>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="topup-confirm-send"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirm & send
          </button>
        </div>
      </div>
    </div>
  );
}
